import type { PublicCardCheckoutRequest } from "@pkg/sdk/models";
import {
  connectToCardIframe,
  type IFrameConnection,
  type CardInputType as PenpalCardInputType,
  type CheckoutResponse as PenpalCheckoutResponse,
} from "../penpal/connect-card";

// Re-export types for public API
export type CardInputType = PenpalCardInputType;
export type CheckoutResponse = PenpalCheckoutResponse;

import { AnalyticsService } from "../analytics";
import { TunnelXManager } from "../penpal/connect-tunnel-x";
import type { CardSubmitOptions, PaymentKitErrors, PaymentKitFields, PaymentKitStates, TInternalFuncs } from "../types";
import {
  $,
  airwallexDeviceIdForRequest,
  awaitFingerprintSettled,
  collect3dsBrowserInfo,
  collectFraudMetadata,
  createCheckoutIFrame,
  definePaymentMethod,
  validateFormFields,
  withRequestId,
} from "../utils";
import { handleMandateConsent, handleNextAction } from "./next-action-handlers";
import { waitForRadarSessionAtSubmit } from "./stripe-radar-session";
import {
  type CardBrandCallback,
  createVgsCardFields,
  createVgsCvcOnlyField,
  initVgsCollect,
  loadVgsCollectScript,
} from "./vgs-collect-loader";

/**
 * Splits a full name into first name and last name.
 * First word becomes firstName, rest becomes lastName.
 */
export const splitName = (fullName: string): { firstName: string; lastName?: string } => {
  const trimmed = fullName.trim();
  const spaceIndex = trimmed.indexOf(" ");
  if (spaceIndex === -1) {
    return { firstName: trimmed };
  }
  return {
    firstName: trimmed.substring(0, spaceIndex),
    lastName: trimmed.substring(spaceIndex + 1).trim() || undefined,
  };
};

/**
 * Maps PaymentKitFields to the customerInfo structure expected by the API.
 */
const mapFieldsToCustomerInfo = (fields: PaymentKitFields) => {
  const { firstName, lastName } = splitName(fields.customer_name);

  const hasBillingAddress =
    fields.customer_country ||
    fields.customer_zip_code ||
    fields.customer_address_line1 ||
    fields.customer_address_line2 ||
    fields.customer_city ||
    fields.customer_state;

  const hasShippingAddress =
    fields.shipping_address_line1 ||
    fields.shipping_address_line2 ||
    fields.shipping_city ||
    fields.shipping_state ||
    fields.shipping_zip_code ||
    fields.shipping_country;

  let taxIds: string[] | undefined;
  if (fields.customer_tax_ids) {
    try {
      taxIds = JSON.parse(fields.customer_tax_ids);
    } catch {
      taxIds = undefined;
    }
  }

  return {
    email: fields.customer_email || undefined,
    firstName,
    lastName,
    businessName: fields.customer_business_name || undefined,
    taxIds: taxIds?.length ? taxIds : undefined,
    billingAddress: hasBillingAddress
      ? {
          country: fields.customer_country || undefined,
          zipCode: fields.customer_zip_code || undefined,
          line1: fields.customer_address_line1 || undefined,
          line2: fields.customer_address_line2 || undefined,
          city: fields.customer_city || undefined,
          state: fields.customer_state || undefined,
        }
      : undefined,
    shippingAddress: hasShippingAddress
      ? {
          line1: fields.shipping_address_line1 || undefined,
          line2: fields.shipping_address_line2 || undefined,
          city: fields.shipping_city || undefined,
          state: fields.shipping_state || undefined,
          zipCode: fields.shipping_zip_code || undefined,
          country: fields.shipping_country || undefined,
        }
      : undefined,
  };
};

type CardStates = PaymentKitStates & {
  cardSetupIntentId?: string;
  cardInputConnections: Partial<Record<PenpalCardInputType, IFrameConnection>>;
  vgsForm?: VGSCollectForm;
  vgsFieldsCleanup?: () => void;
  // Blank-form (card_*: required) telemetry signals read at the submit-failure sites.
  // Set true once the VGS Collect CDN script resolves (card fields can render).
  vgsCdnLoaded?: boolean;
  // Set true on the shopper's first focus of any VGS card field.
  anyCardFieldFocused?: boolean;
  vgsFocusCallbacks?: Partial<Record<PenpalCardInputType, (isFocused: boolean) => void>>;
  vgsBrandCallbacks?: Partial<Record<PenpalCardInputType, CardBrandCallback>>;
  vgsMountSelectors?: Partial<Record<PenpalCardInputType, string>>;
  vgsPlaceholders?: Partial<Record<PenpalCardInputType, string>>;
  // Ids of hidden VGS 3DS device-fingerprint iframes injected on tokenize (for cleanup()).
  fingerprintIframeIds?: string[];
  // Saved-card one-click CVC re-collection (CIT_SAVED_CARD_ONE_CLICK §14). A SEPARATE
  // VGS Collect form holding only the CVC field, so mounting it initialises VGS
  // without the "all three card fields" guard the PAN mount trips. Tokenized to a
  // fresh saved_card_cvc_token at confirm via the OUTBOUND CMP Card Update API
  // (PATCH /cards/{cmpCardId}) — no inbound REDACT route, no tunnel.
  savedCardCvcForm?: VGSCollectForm;
  savedCardCvcFieldsCleanup?: () => void;
  savedCardCvcMounted?: boolean;
  // The vaulted card's CMP card id (CRD…) threaded from the saved-cards list when the
  // CVC element mounts, so the outbound CMP Card Update knows which card to PATCH.
  savedCardCvcCmpCardId?: string;
};

type CreateElementOptions = Partial<Parameters<typeof connectToCardIframe>[1]> & {
  style?: Record<string, string>;
  placeholder?: string;
  /**
   * VGS mode only. Fires as the shopper types the card number, the moment VGS
   * recognizes the brand — the PaymentKit equivalent of NMI Collect.js
   * `lookUpCallback`. Lets merchants flag unsupported brands (e.g. Mastercard on
   * peptide MIDs) before submit. Pass it on the `card_pan` element; only the
   * card-number field carries a brand.
   *
   * `brand` is VGS's own lowercase network token ("visa", "mastercard", "amex", …)
   * or null when the field is empty / unrecognized. It is passed through verbatim —
   * NOT normalized to a PaymentKit-canonical vocabulary — so compare it
   * case-insensitively and do not assume it equals the `card_brand` the backend
   * returns on confirm. No-op in direct/KMS tokenization mode (the callback simply
   * never fires); tokenization mode is server-decided, not merchant-set.
   */
  onCardBrand?: CardBrandCallback;
};

/**
 * Logs a swallowed cleanup/teardown failure and reports it to analytics — teardown failures
 * are caught (rather than thrown) so one step's failure can't abort the rest of cleanup, but
 * a swallowed error is otherwise invisible: it lands in the *merchant's* browser console,
 * which we never read. Without this, PaymentKit has no way to learn a teardown guard fired
 * in the field short of a merchant reporting it manually (as happened for PAY-1741).
 */
const reportCleanupFailure = (stage: string, err: unknown) => {
  console.warn(`[PaymentKit] cleanup step failed (${stage}):`, err);
  AnalyticsService.capture("paymentkit_cleanup_teardown_failed", {
    stage,
    error: err instanceof Error ? err.message : String(err),
  });
};

/**
 * Tears down the VGS Collect form and its field iframes, if present. Called both when a
 * single card field unmounts (React strict-mode remount, field-level teardown) and from
 * the payment method's top-level cleanup() — kept as one function so a future VGS teardown
 * change can't land in only one of the two call sites (see PAY-1741).
 *
 * VGS Collect mounts directly into merchant-owned DOM containers, so by the time this runs
 * the merchant's own re-render may have already removed those containers. VGS's internal
 * unmount then throws trying to removeChild() a node that's no longer attached — non-fatal,
 * so it's caught and reported rather than allowed to abort the rest of PaymentKit's cleanup.
 * TODO(PAY-1742): mount VGS fields into a PaymentKit-owned container div (matching
 * stripe-link-adapter.ts) so this race — and this guard — stop being necessary at all.
 */
const teardownVgsForm = (states: CardStates) => {
  if (!states.vgsForm) return;
  try {
    states.vgsFieldsCleanup?.();
  } catch (err) {
    reportCleanupFailure("card:vgs_fields_cleanup", err);
  }
  states.vgsFieldsCleanup = undefined;
  try {
    states.vgsForm.unmount();
  } catch (err) {
    reportCleanupFailure("card:vgs_form_unmount", err);
  }
  states.vgsForm = undefined;
};

const defCreateElement = (states: CardStates) => {
  const { baseUrl, apiBaseUrl, cardInputConnections, secureToken, timingTracker } = states;

  return (type: PenpalCardInputType, options: CreateElementOptions) => {
    const { style, onLoaded, onFocusChange, placeholder, onCardBrand } = options;

    const mountIFrame = (parentSelector: string) => {
      // Mutable cleanup callbacks — set once mode is determined and mount completes
      let cleanupVgs: (() => void) | undefined;
      let cleanupDirect: (() => void) | undefined;
      // Cancellation flag: prevents deferred doMount from executing after unmount.
      // Needed because React strict mode may call unmount before the deferred
      // _sessionConfigReady promise resolves.
      let cancelled = false;

      /**
       * Performs the actual DOM mount after the tokenization mode is known.
       * Runs synchronously when mode is explicitly provided, or after the
       * auto-detect fetch resolves.
       */
      // Store mount selector so VGS init (triggered by card_pan) can read
      // the exp/cvc selectors that were registered by their own createElement calls.
      if (!states.vgsMountSelectors) states.vgsMountSelectors = {};
      states.vgsMountSelectors[type] = parentSelector;

      const doMount = () => {
        if (cancelled) return;
        const isVgsMode = states.cardTokenizationMode === "vgs";
        console.log(
          `[PaymentKit] card.doMount(${type}) — mode: ${states.cardTokenizationMode}, vgsVaultId: ${states.vgsVaultId ?? "unset"}, vgsEnvironment: ${states.vgsEnvironment ?? "unset"}`,
        );

        // VGS mode: initialize VGS Collect form and mount fields on card_pan
        if (isVgsMode && states.vgsVaultId && states.vgsEnvironment) {
          // Store per-field focus callbacks and placeholders so createVgsCardFields
          // can wire each field to its own callback/placeholder. All three createElement
          // calls complete before the CDN script loads, so all values are available by then.
          if (onFocusChange) {
            if (!states.vgsFocusCallbacks) states.vgsFocusCallbacks = {};
            states.vgsFocusCallbacks[type] = onFocusChange;
          }
          if (placeholder) {
            if (!states.vgsPlaceholders) states.vgsPlaceholders = {};
            states.vgsPlaceholders[type] = placeholder;
          }
          if (onCardBrand) {
            if (!states.vgsBrandCallbacks) states.vgsBrandCallbacks = {};
            states.vgsBrandCallbacks[type] = onCardBrand;
          }

          if (type === "card_pan") {
            const vaultId = states.vgsVaultId;
            const vgsEnv = states.vgsEnvironment;

            console.log(`[PaymentKit] VGS mode active — loading VGS Collect CDN for vault ${vaultId} (${vgsEnv})...`);
            const VGS_CDN_TIMEOUT_MS = 15_000;
            let cdnTimeoutId: ReturnType<typeof setTimeout>;
            const cdnLoadStart = performance.now();
            Promise.race([
              loadVgsCollectScript(),
              new Promise<never>((_, reject) => {
                cdnTimeoutId = setTimeout(() => {
                  console.warn(`[PaymentKit] VGS Collect CDN load timed out after ${VGS_CDN_TIMEOUT_MS / 1000}s`);
                  reject(new Error("VGS Collect CDN load timed out"));
                }, VGS_CDN_TIMEOUT_MS);
              }),
            ])
              .finally(() => clearTimeout(cdnTimeoutId))
              .then(() => {
                // The CDN script resolved — card fields can render. Recorded for the
                // state-at-submit snapshot (cdn_loaded) regardless of cancellation.
                states.vgsCdnLoaded = true;
                if (cancelled) return;
                console.log("[PaymentKit] VGS Collect CDN loaded — initializing form...");
                const form = initVgsCollect(vaultId, vgsEnv);
                if (cancelled) {
                  form.unmount();
                  return;
                }
                states.vgsForm = form;

                const css = style
                  ? Object.fromEntries(
                      Object.entries(style).map(([k, v]) => [k.replace(/([A-Z])/g, "-$1").toLowerCase(), v]),
                    )
                  : undefined;

                // Read exp/cvc selectors from shared state.
                // Each createElement("card_exp/card_cvc").mount(selector) stores its
                // selector before this async callback runs, so they're available here.
                const expSelector = states.vgsMountSelectors?.card_exp;
                const cvcSelector = states.vgsMountSelectors?.card_cvc;
                if (!expSelector || !cvcSelector) {
                  const msg =
                    `[PaymentKit] VGS mode requires all three card fields to be mounted. ` +
                    `Missing: ${!expSelector ? "card_exp" : ""}${!expSelector && !cvcSelector ? ", " : ""}${!cvcSelector ? "card_cvc" : ""}`;
                  console.error(msg);
                  onLoaded?.();
                  return;
                }
                console.log(
                  `[PaymentKit] Creating VGS card fields — pan: ${parentSelector}, exp: ${expSelector}, cvc: ${cvcSelector}`,
                );
                states.vgsFieldsCleanup = createVgsCardFields(
                  form,
                  {
                    pan: parentSelector,
                    exp: expSelector,
                    cvc: cvcSelector,
                  },
                  css,
                  states.vgsFocusCallbacks,
                  states.vgsPlaceholders,
                  states.vgsBrandCallbacks,
                  (field) => {
                    states.anyCardFieldFocused = true;
                    timingTracker.trackFirstInput(field);
                  },
                );

                console.log("[PaymentKit] VGS card fields created successfully");
                timingTracker.trackCardMount();
                timingTracker.trackInputReady();
                onLoaded?.();
              })
              .catch((err) => {
                console.error("[PaymentKit] Failed to load VGS Collect script. Card input will not be available:", err);
                // Blank-form telemetry: the CDN never loaded, so no card fields render
                // and a later submit fails with card_*: required. Separate the 15s CDN
                // timeout from a script onerror (the race rejects with distinct messages).
                const reason =
                  err instanceof Error && err.message === "VGS Collect CDN load timed out"
                    ? "cdn_timeout"
                    : "cdn_error";
                timingTracker.trackCardMountFailed(reason, Math.round(performance.now() - cdnLoadStart));
                // Signal loaded so the form exits loading state rather than hanging forever.
                // Card fields won't render, but submit will fail with a clear error.
                onLoaded?.();
              });
          }

          // For non-pan fields in VGS mode, signal loaded immediately since
          // VGS handles all three fields from the card_pan mount.
          if (type !== "card_pan") {
            onLoaded?.();
          }

          cleanupVgs = () => {
            if (type === "card_pan") {
              teardownVgsForm(states);
            }
          };
          return;
        }

        // If we got here and mode was "vgs", it means VGS config was incomplete
        if (isVgsMode) {
          console.warn(
            `[PaymentKit] VGS mode requested but config incomplete (vgsVaultId: ${states.vgsVaultId ?? "unset"}, vgsEnvironment: ${states.vgsEnvironment ?? "unset"}) — using direct mode`,
          );
        }

        // Direct mode: existing penpal iframe logic
        const parent = $(parentSelector) as HTMLElement;

        // Read container padding so the embed input matches the merchant's
        // visual intent. The iframe uses absolute positioning to fill the
        // full container, so the input needs its own padding for alignment.
        const computed = getComputedStyle(parent);
        const containerPadding = computed.padding !== "0px" ? computed.padding : undefined;
        const enhancedStyle = {
          ...(style ?? {}),
          height: "100%",
          boxSizing: "border-box",
          ...(containerPadding ? { padding: containerPadding } : {}),
        };

        const params: Record<string, string> = {
          checkout_token: secureToken,
          api_base_url: apiBaseUrl,
          style: JSON.stringify(enhancedStyle),
        };

        const iframe = createCheckoutIFrame(type.replace("_", "-"), baseUrl, params);

        // Use absolute positioning so the iframe covers the full container
        // including any padding the merchant's CSS applies.
        if (computed.position === "static") {
          parent.style.position = "relative";
        }
        iframe.style.position = "absolute";
        iframe.style.inset = "0";

        // Set cleanup before DOM mutation to avoid leaking the iframe if
        // unmount() is called between appendChild and onload.
        cleanupDirect = () => {
          const connection = cardInputConnections[type];
          connection?.destroy();
          iframe.remove();
          states.cardInputConnections[type] = undefined;
        };

        parent.appendChild(iframe);

        iframe.onload = () => {
          if (type === "card_pan") {
            timingTracker.trackInputReady();
          }

          const connection = connectToCardIframe(iframe, {
            onLoaded: onLoaded || (() => {}),
            onFocusChange: onFocusChange || (() => {}),
          });

          states.cardInputConnections[type] = connection;
        };
      };

      // If session config needs auto-detection, wait for it; otherwise mount synchronously.
      // `_sessionConfigReady` never rejects — a terminal failure is carried on `sessionConfigError`.
      if (states._sessionConfigReady) {
        states._sessionConfigReady.then(() => {
          if (cancelled) return;
          if (states.sessionConfigError) {
            // Config failed terminally (no direct-mode fallback): the tokenization mode is unknown,
            // so there is nothing to mount. Release the loading state so the form doesn't spin
            // forever — a later submit surfaces the failure to the merchant's onError.
            console.error(
              "[PaymentKit] card element not mounted — session config unavailable:",
              stringifyError(states.sessionConfigError),
            );
            onLoaded?.();
            return;
          }
          doMount();
        });
      } else {
        doMount();
      }

      return {
        unmount: () => {
          cancelled = true;
          cleanupVgs?.();
          cleanupDirect?.();
        },
      };
    };

    return { mount: mountIFrame };
  };
};

/**
 * Terminal failure states for a card checkout. Mirrors the backend checkout-attempt state machine
 * (app/modules/checkout/checkout_attempt/checkout_attempt_state_machine.py — the only terminal
 * success state is `checkout_succeeded`). A checkout in one of these states must be surfaced to the
 * merchant as an error so `onError` — not `onSuccess` — fires.
 *
 * Note: 3DS authentication *passing* does NOT imply the charge succeeded — a pass-then-decline
 * concludes with `payment_failed` and no further `nextAction`. Other non-terminal states (e.g.
 * `payment_started` from an async "pending" charge that carries no `nextAction`) are intentionally
 * left to the success/data path here, preserving existing behavior for the non-3DS flow.
 */
const CHECKOUT_FAILURE_STATES = new Set(["payment_failed", "checkout_failed"]);

const isCheckoutFailure = (result: CheckoutResponse): boolean => CHECKOUT_FAILURE_STATES.has(result.state);

/** Result shape shared by the fresh-card submit and the saved-card confirm. */
type CardCheckoutResult =
  | { data: { [key: string]: unknown }; errors?: never }
  | { data?: never; errors: PaymentKitErrors };

/**
 * Drives a card checkout to conclusion: RBI e-mandate consent pauses, the 3DS
 * next-action loop (handleNextAction → cardCheckoutVerify), processor cascade, and
 * the final success/failure classification. Shared VERBATIM by BOTH the
 * fresh-card submit (`defSubmitPayment`) and the saved-card one-click confirm
 * (`defConfirmSavedCard`) — the ONLY difference between the two paths is the
 * `runCardCheckout` closure that issues the confirm request (fresh setup intent vs
 * saved payment method). 3DS is NOT reimplemented here: the saved-card reuse charge
 * runs the exact same real loop, so a vaulted card that triggers 3DS is handled
 * identically to a freshly-typed one.
 */
const runCardCheckoutLoop = async (
  states: CardStates,
  tunnelX: Awaited<ReturnType<typeof TunnelXManager.createFromPenpalConnection>>,
  requestOptions: ReturnType<typeof withRequestId>,
  cardOptions: CardSubmitOptions | undefined,
  runCardCheckout: (indiaMandateConsentFields: Record<string, unknown>) => Promise<CheckoutResponse>,
): Promise<CardCheckoutResult> => {
  const { timingTracker } = states;

  // First submit: forward any acceptance the caller already supplied (manual mode, or an
  // older merchant-built gate that resubmits via kit.submit). Empty otherwise.
  const initialConsentFields = cardOptions?.indiaMandateConsent?.accepted
    ? {
        indiaMandateAccepted: true,
        indiaMandateConsentVersion: cardOptions.indiaMandateConsent.consentVersion,
      }
    : {};
  let currentResult = await runCardCheckout(initialConsentFields);

  console.log("Card checkout result:", currentResult);

  // Step 6. Handle next actions in a loop (supports multiple 3DS challenges)
  // This loop handles the case where one processor fails and we try another
  // that also requires 3DS authentication.
  const MAX_USER_ACTIONS = 5; // Safety limit to prevent infinite loops
  let userActionCount = 0;

  while (currentResult.nextAction && userActionCount < MAX_USER_ACTIONS) {
    // RBI e-mandate consent (PAY-1716) is a HUMAN pause, NOT an auto-driven 3DS action.
    // It is handled HERE, before handleNextAction / cardCheckoutVerify — that verify path
    // is the 3DS auto-resume and records NO consent, so routing the mandate action through
    // it would defeat the required explicit affirmative consent (the kernel). The surface is
    // configurable (default = a PKJS-rendered modal):
    //   - modal (default) / inline → PKJS renders OUR consent surface; on tick + Accept it
    //     RESUBMITS the checkout with the acceptance (re-entering this loop past the reveal),
    //     and on cancel it fails the flow.
    //   - manual → return the action so the merchant renders its OWN unchecked-by-default
    //     gate and resubmits via kit.submit({ indiaMandateConsent: {...} }) (today's behavior).
    // In every mode consent is affirmative: PKJS never auto-ticks and never auto-resubmits.
    if (currentResult.nextAction.type === "stripe_mandate_consent") {
      userActionCount++;
      const uxMode = cardOptions?.indiaMandateConsentUx?.mode ?? "modal";

      if (uxMode === "manual") {
        console.log("Card checkout paused for RBI e-mandate consent — returning action to caller (manual mode)");
        return { data: currentResult as { [key: string]: unknown } };
      }

      console.log(`Card checkout paused for RBI e-mandate consent — rendering ${uxMode} consent surface`);
      // Optional embedder color overrides (nice-to-have) — undefined on the first-party hosted
      // checkout, which themes the modal via the inherited --pk-* vars instead. Only build an
      // options arg when there is something to pass (inline container and/or appearance), so the
      // default modal path stays a plain `handleMandateConsent(action)` with no options.
      const consentAppearance = cardOptions?.indiaMandateConsentUx?.appearance;
      const consentOptions =
        uxMode === "inline"
          ? { inlineContainer: cardOptions?.indiaMandateConsentUx?.container, appearance: consentAppearance }
          : consentAppearance
            ? { appearance: consentAppearance }
            : undefined;
      const consentResult = await handleMandateConsent(currentResult.nextAction, consentOptions);

      if (!consentResult.accepted) {
        console.log("RBI e-mandate consent was not given — concluding checkout without a charge");
        timingTracker.trackFail(
          currentResult.checkoutAttemptId || null,
          "india_mandate_consent_declined",
          "Payment not authorized — e-mandate consent was not given.",
        );
        return { errors: { root: "Payment not authorized — e-mandate consent was not given." } };
      }

      // Payer ticked the box AND clicked Accept → resubmit with the acceptance so the backend
      // records it as dispute evidence and proceeds into the single-step SetupIntent mandate
      // registration. NOT routed through handleNextAction / cardCheckoutVerify.
      currentResult = await runCardCheckout({
        indiaMandateAccepted: true,
        indiaMandateConsentVersion: consentResult.consentVersion,
      });
      continue;
    }

    userActionCount++;
    console.log(`Handling user action ${userActionCount}/${MAX_USER_ACTIONS}...`);

    const actionResult = await handleNextAction(currentResult.nextAction);

    // Native 3DS2 (Adyen) produces the fingerprint / challenge result client-side as
    // the component's state.data; forward it to the verify endpoint (→ Adyen
    // /payments/details). Redirect / VGS flows carry no client result, so this is
    // omitted and the body-less verify keeps its existing behavior. Narrow the
    // opaque data and only send when there is actual payload, so a bare {} never
    // displaces the None the backend treats as "redirect / VGS, no client result".
    const verifyData = actionResult.success ? actionResult.data : undefined;
    const rawDetails = verifyData?.details;
    const rawPaymentData = verifyData?.paymentData;
    const details = rawDetails && typeof rawDetails === "object" ? (rawDetails as Record<string, unknown>) : undefined;
    const paymentData = typeof rawPaymentData === "string" ? rawPaymentData : undefined;
    const card3dsVerifyRequest = details || paymentData ? { details, paymentData } : undefined;

    // Always call verify endpoint so backend can properly conclude the checkout.
    // This is needed even on 3DS failure so the backend can transition from
    // user_actions_requested to card_payment_concluded with proper failure state.
    console.log("User action completed, verifying checkout...");
    const verifyResult = await tunnelX.publicEndpoints.cardCheckoutVerify(
      {
        checkoutToken: states.secureToken,
        ...(card3dsVerifyRequest ? { card3dsVerifyRequest } : {}),
      },
      requestOptions,
    );

    // Check if another action is required (e.g., cascade to next processor with 3DS)
    // This must be checked BEFORE returning error, as cascade may offer a new 3DS challenge
    if (verifyResult.nextAction) {
      if (!actionResult.success) {
        console.log("3DS failed but cascade triggered new action, continuing loop...");
      } else {
        console.log("Another user action required, continuing loop...");
      }
      currentResult = verifyResult;
      continue;
    }

    // The backend verify (cardCheckoutVerify) is the source of truth for the final
    // outcome — a frontend actionResult.success=false (e.g. a VGS modal dismissed
    // before the redirect posted `3ds_complete`) must NOT override a backend-concluded
    // success, or the shopper sees an error for a charge that actually went through.
    const backendConcludedSuccess = verifyResult.state === "checkout_succeeded";

    // Backend concluded a failure (pass-then-decline, 3DS failure) → surface it,
    // using actionResult.error only as a fallback message.
    if (isCheckoutFailure(verifyResult)) {
      console.log("Card checkout concluded in a failure state after 3DS:", verifyResult);
      const actionError = actionResult.success ? undefined : actionResult.error;

      timingTracker.trackFail(
        verifyResult.checkoutAttemptId || null,
        verifyResult.errorCode || "payment_failed",
        verifyResult.errorMessageForCustomer || actionError || "Payment failed",
      );
      return {
        errors: {
          root: verifyResult.errorMessageForCustomer || actionError || "Payment failed",
          checkout_response: verifyResult,
        },
      };
    }

    // Frontend action failed AND the backend did not conclude a definitive success
    // (ambiguous/pending) → trust the frontend failure (e.g. genuine 3DS
    // failure/cancellation with no charge). Preserves Stripe/Airwallex semantics.
    if (!actionResult.success && !backendConcludedSuccess) {
      console.log("3DS action failed and no backend success/cascade, concluded:", verifyResult);
      timingTracker.trackFail(
        verifyResult.checkoutAttemptId || null,
        verifyResult.errorCode || "3ds_failed",
        verifyResult.errorMessageForCustomer || actionResult.error,
      );
      return {
        errors: {
          root: verifyResult.errorMessageForCustomer || actionResult.error,
          checkout_response: verifyResult,
        },
      };
    }

    console.log("Card checkout verified ✅", verifyResult);

    // No more actions needed, return the result
    timingTracker.trackSuccess(verifyResult.checkoutAttemptId || "unknown");
    return { data: verifyResult as { [key: string]: unknown } };
  }

  if (userActionCount >= MAX_USER_ACTIONS) {
    console.error("Max user actions exceeded");
    timingTracker.trackFail(null, "max_actions_exceeded", "Too many authentication attempts");
    return { errors: { root: "Too many authentication attempts. Please try again." } };
  }

  // Check if checkout succeeded or failed
  // States: checkout_succeeded (success), payment_failed (failure)
  if (isCheckoutFailure(currentResult)) {
    console.log("Card checkout failed:", currentResult);
    timingTracker.trackFail(
      currentResult.checkoutAttemptId || null,
      currentResult.errorCode || "payment_failed",
      currentResult.errorMessageForCustomer || "Payment failed",
    );
    // Return the full response as errors so frontend can display error details
    // including error_code, error_message_for_customer, error_message_for_debug
    return {
      errors: {
        root: currentResult.errorMessageForCustomer || "Payment failed",
        checkout_response: currentResult,
      },
    };
  }

  console.log("Card checkout completed ✅", currentResult);
  timingTracker.trackSuccess(currentResult.checkoutAttemptId || "unknown");
  return { data: currentResult as { [key: string]: unknown } };
};

const defSubmitPayment = (states: CardStates) => {
  const submitPayment: TInternalFuncs["submitPayment"] = async (fields, options) => {
    // Ensure session config is loaded (no-op if explicitly provided). It never rejects; a terminal
    // failure is carried on `sessionConfigError`, which we re-throw here so the submit wrapper in
    // index.ts routes it to the merchant's onError (same end behavior as the old rejection).
    if (states._sessionConfigReady) await states._sessionConfigReady;
    if (states.sessionConfigError) throw states.sessionConfigError;
    const isVgsMode = states.cardTokenizationMode === "vgs";

    const { timingTracker, checkoutRequestId } = states;

    // Track submit event
    timingTracker.trackSubmit();

    // Use the checkout request ID from states (generated on page load)
    const requestOptions = withRequestId(checkoutRequestId);

    const tunnelX = await TunnelXManager.createFromPenpalConnection(states.tunnelXConnection);

    // Step 1. Validate form values.
    // Form field validation is shared across both modes.
    // In VGS mode, card field validation is handled by VGS Collect (built-in validators).
    // In direct mode, card field validation is handled by penpal iframes.
    const skipCustomerValidation = options?.skipCustomerValidation === true;
    const validateFormResult = skipCustomerValidation
      ? { isSuccess: true as const, errors: {} as PaymentKitErrors }
      : await validateFormFields(fields);
    if (!isVgsMode) {
      const validateCardResult = await validateCardFields(states);
      if (!(validateCardResult.isSuccess && validateFormResult.isSuccess)) {
        timingTracker.trackFail(null, "validation_error", "Form validation failed");
        return { errors: { ...validateCardResult.errors, ...validateFormResult.errors } };
      }
    } else if (!validateFormResult.isSuccess) {
      timingTracker.trackFail(null, "validation_error", "Form validation failed");
      return { errors: validateFormResult.errors };
    }

    // Step 2. Create card setup intent if not present
    if (!states.cardSetupIntentId) {
      const res = await tunnelX.publicEndpoints.createCardSetupIntent({
        checkoutToken: states.secureToken,
      });
      states.cardSetupIntentId = res.cardSetupIntentId;
    }

    // Step 3. Submit card values.
    if (isVgsMode) {
      // VGS mode: create card via CMP, then send aliases to backend
      console.log("[PaymentKit] Submitting card via VGS CMP...");
      const vgsSubmitResult = await submitVgsCardFields(states, tunnelX);
      if (!vgsSubmitResult.isSuccess) {
        // State-at-submit snapshot for the blank-form failures. Per-field *_set are not
        // yet determinable here — the card setup intent has not been fetched.
        timingTracker.trackSubmitState({
          vgsFormPresent: states.vgsForm != null,
          cdnLoaded: states.vgsCdnLoaded ?? false,
          anyFieldFocused: states.anyCardFieldFocused ?? false,
        });
        // Include field-level error details for PostHog visibility
        const errorDetail = Object.entries(vgsSubmitResult.errors)
          .map(([k, v]) => `${k}: ${v}`)
          .join(", ");
        timingTracker.trackFail(null, "card_submit_error", errorDetail || "VGS card submission failed");
        return { errors: vgsSubmitResult.errors };
      }
    } else {
      // Direct mode: submit via penpal iframes
      const submitCardResult = await submitCardFields(states);
      if (!submitCardResult.isSuccess) {
        timingTracker.trackFail(null, "card_submit_error", "Card submission failed");
        return { errors: submitCardResult.errors };
      }
    }

    // Step 4. Get card setup intent.
    const cardSetupIntent = await tunnelX.publicEndpoints.getCardSetupIntent({
      cardSetupIntentId: states.cardSetupIntentId,
      checkoutToken: states.secureToken,
    });
    if (!cardSetupIntent.isCardAllSet) {
      const errors = {} as PaymentKitErrors;
      if (!cardSetupIntent.isCardPanSet) errors.card_pan = "required";
      if (!cardSetupIntent.isCardExpSet) errors.card_exp = "required";
      if (!cardSetupIntent.isCardCvcSet) errors.card_cvc = "required";
      // State-at-submit snapshot + per-field breakdown for the blank-form failures. The
      // backend reports exactly which fields are unset, so an all-blank wipe is separable
      // from a single missing field (e.g. cvc-only) — previously this detail was dropped.
      timingTracker.trackSubmitState({
        vgsFormPresent: states.vgsForm != null,
        cdnLoaded: states.vgsCdnLoaded ?? false,
        anyFieldFocused: states.anyCardFieldFocused ?? false,
        panSet: cardSetupIntent.isCardPanSet,
        expSet: cardSetupIntent.isCardExpSet,
        cvcSet: cardSetupIntent.isCardCvcSet,
      });
      const missingFields = Object.keys(errors);
      timingTracker.trackFail(
        null,
        "card_setup_incomplete",
        missingFields.length
          ? `Card details incomplete (missing: ${missingFields.join(", ")})`
          : "Card details incomplete",
      );
      return { errors };
    }

    console.log("Card setup intent is set ✅", cardSetupIntent);
    console.log("Fields", fields);

    // Step 5. Submit card checkout with customer info and fraud metadata
    // Include processor_fraud_info with airwallex_device_id for fraud fingerprinting.
    // browserInfo always carries the general fraud telemetry (userAgent/language/
    // timezone) for ALL modes — it is persisted verbatim on the checkout attempt.
    // In VGS mode we additionally send the VGS-specific 3DS fingerprint fields in a
    // SEPARATE threeDsBrowserInfo field (backend reads fraud_metadata.three_ds_browser_info
    // for authenticate), so the VGS payload never carries telemetry keys and the
    // telemetry never loses language/timezone. Airwallex fraud uses processorFraudInfo.
    // 3DS browser info + origin/returnUrl are sent for the CARD path in ALL
    // tokenization modes: VGS 3DS reads them, and native Adyen 3DS2 needs them even
    // in VGS mode (CARD_TOKENIZATION_MODE defaults to `vgs` everywhere). origin is the
    // bare checkout host; returnUrl is the checkout page URL (protocol-inclusive). The
    // general telemetry from collectFraudMetadata() is left untouched.
    // Stripe Radar session (PAY-1726): carries client IP/device into the Stripe
    // PaymentIntent for Visa CE 3.0 / Mastercard FPT dispute deflection. Minted at
    // init (PAY-2001); this only waits, bounded, for that mint. Best-effort —
    // undefined (and omitted below) on any failure, so the charge is never blocked.
    const stripeRadarSession = await waitForRadarSessionAtSubmit(states._radarSessionMint, {
      checkoutSessionId: states.secureToken,
      requestId: states.checkoutRequestId,
    });
    // Let the Airwallex fingerprint script settle (bounded) right where its id is
    // read: by now the card setup round trips have run and it almost always has.
    // Cards have no sheet, so no gesture risk (PAY-1538).
    await awaitFingerprintSettled(states._fraudScriptsReady);
    const airwallexDeviceId = airwallexDeviceIdForRequest(states.checkoutRequestId);
    const fraudMetadata = {
      ...collectFraudMetadata(),
      clientVersion: states.clientVersion,
      threeDsBrowserInfo: collect3dsBrowserInfo(),
      threeDsOrigin: window.location.origin,
      threeDsReturnUrl: window.location.href,
      processorFraudInfo: {
        // Only once Airwallex's fingerprint script has loaded (same rule as the
        // hand-built bodies), so a card and a wallet attempt never disagree.
        // airwallexFingerprintCollected: this PaymentKit.js saw the script's own
        // onload. Older versions sent the id unconditionally; the backend logs the
        // difference so the two populations are separable.
        ...(airwallexDeviceId ? { airwallexDeviceId, airwallexFingerprintCollected: true } : {}),
        ...(stripeRadarSession ? { stripeRadarSession } : {}),
      },
    };
    const cardOptions = options as CardSubmitOptions | undefined;
    // Build the card-checkout body once; only the RBI e-mandate consent fields vary between
    // the first submit and the post-consent resubmit (PAY-1716), so the resubmit stays
    // byte-identical to the original apart from the acceptance. The generated SDK model uses
    // camelCase keys (serialized back to the snake_case wire contract `india_mandate_accepted`
    // / `india_mandate_consent_version`); passing snake_case here makes the client's zod strip
    // them and silently drop the consent.
    const runCardCheckout = (indiaMandateConsentFields: Record<string, unknown>) =>
      tunnelX.publicEndpoints.cardCheckout(
        {
          checkoutToken: states.secureToken,
          publicCardCheckoutRequest: {
            cardSetupIntentId: states.cardSetupIntentId,
            customerInfo: {
              ...mapFieldsToCustomerInfo(fields),
              // kit.submit passthrough (PAY-1671 / #3785): only processor_overrides is honored
              // today (the per-processor Stripe email override). Maps onto the request's
              // processorOverrides so a checkout-born customer carries it; primary email untouched.
              processorOverrides: cardOptions?.extraParamsForCustomerCreation?.processor_overrides as
                | Record<string, { email?: string }>
                | undefined,
            },
            fraudMetadata,
            // RBI e-mandate capability (PAY-1716 backwards-compat): this integrated build can
            // render the stripe_mandate_consent next_action, so advertise it on EVERY card submit
            // (first and consent resubmit), independent of whether the payer has accepted yet.
            // Merchants on an older PKJS build never send it and stay on the pre-RBI no-mandate
            // path (auto-ungating). The regenerated PublicCardCheckoutRequest SDK model serializes
            // the camelCase supportsIndiaMandate back to the snake_case wire field
            // supports_india_mandate, exactly as the consent fields do.
            supportsIndiaMandate: true,
            ...indiaMandateConsentFields,
          } as PublicCardCheckoutRequest,
        },
        requestOptions,
      );

    // Step 6+. Drive the checkout to conclusion through the SHARED loop (3DS,
    // RBI e-mandate consent, cascade, verify). The saved-card confirm path below
    // reuses this exact loop, so 3DS is never duplicated between the two.
    return runCardCheckoutLoop(states, tunnelX, requestOptions, cardOptions, runCardCheckout);
  };
  return submitPayment;
};

/** camelCase style object → the kebab-case CSS VGS Collect expects. */
const toKebabCss = (style: Record<string, string>): Record<string, string> =>
  Object.fromEntries(Object.entries(style).map(([k, v]) => [k.replace(/([A-Z])/g, "-$1").toLowerCase(), v]));

/** Tears down the standalone saved-card CVC VGS form (mirrors teardownVgsForm). */
const teardownSavedCardCvcForm = (states: CardStates) => {
  states.savedCardCvcMounted = false;
  states.savedCardCvcCmpCardId = undefined;
  if (!states.savedCardCvcForm) return;
  try {
    states.savedCardCvcFieldsCleanup?.();
  } catch (err) {
    reportCleanupFailure("card:saved_cvc_fields_cleanup", err);
  }
  states.savedCardCvcFieldsCleanup = undefined;
  try {
    states.savedCardCvcForm.unmount();
  } catch (err) {
    reportCleanupFailure("card:saved_cvc_form_unmount", err);
  }
  states.savedCardCvcForm = undefined;
};

type SavedCardCvcElementOptions = {
  style?: Record<string, string>;
  placeholder?: string;
  onFocusChange?: (isFocused: boolean) => void;
  onLoaded?: () => void;
  /**
   * The selected saved card's CMP card id (CRD…), from the saved-cards list. Threaded
   * into state at mount so the outbound CMP Card Update (PATCH /cards/{cmpCardId}) at
   * confirm targets the right card. Re-mount the element when the selected card changes
   * so this stays in sync.
   */
  cmpCardId?: string;
};

/**
 * Mountable CVC-only element for the saved-card one-click (CIT_SAVED_CARD_ONE_CLICK §14).
 *
 * A saved card has no stored security code. When the merchant's CVC re-prompt
 * setting is on, the checkout renders this element so the shopper re-enters the
 * CVC. It decouples VGS init from the PAN mount: mounting only this field spins up
 * a dedicated VGS Collect form with a single `card_cvc` field, so it never trips
 * the full-card "all three fields" guard. The raw digits stay inside the VGS
 * iframe; `confirmSavedCard` tokenizes them to a `saved_card_cvc_token` alias.
 *
 * VGS mode only (the tokenization mode is server-decided). Returns the standard
 * `{ mount }` element shape.
 */
const defCreateSavedCardCvcElement = (states: CardStates) => {
  return (options: SavedCardCvcElementOptions = {}) => {
    const { style, placeholder, onFocusChange, onLoaded, cmpCardId } = options;

    const mount = (parentSelector: string) => {
      let cancelled = false;

      const doMount = () => {
        if (cancelled) return;
        if (states.cardTokenizationMode !== "vgs" || !states.vgsVaultId || !states.vgsEnvironment) {
          console.error(
            `[PaymentKit] saved-card CVC element requires VGS tokenization mode (mode: ${states.cardTokenizationMode ?? "unset"})`,
          );
          onLoaded?.();
          return;
        }
        const vaultId = states.vgsVaultId;
        const vgsEnv = states.vgsEnvironment;

        loadVgsCollectScript()
          .then(() => {
            if (cancelled) return;
            const form = initVgsCollect(vaultId, vgsEnv);
            if (cancelled) {
              form.unmount();
              return;
            }
            states.savedCardCvcForm = form;
            states.savedCardCvcCmpCardId = cmpCardId;
            states.savedCardCvcFieldsCleanup = createVgsCvcOnlyField(
              form,
              parentSelector,
              style ? toKebabCss(style) : undefined,
              onFocusChange,
              placeholder,
            );
            states.savedCardCvcMounted = true;
            console.log("[PaymentKit] saved-card CVC field mounted");
            onLoaded?.();
          })
          .catch((err) => {
            console.error("[PaymentKit] Failed to mount saved-card CVC field:", stringifyError(err));
            onLoaded?.();
          });
      };

      // `_sessionConfigReady` never rejects — a terminal failure is carried on `sessionConfigError`.
      if (states._sessionConfigReady) {
        states._sessionConfigReady.then(() => {
          if (cancelled) return;
          if (states.sessionConfigError) {
            // Config failed terminally (no direct-mode fallback): the mode is unknown, so the CVC
            // field can't mount. Release the loading state.
            console.error(
              "[PaymentKit] saved-card CVC not mounted — session config unavailable:",
              stringifyError(states.sessionConfigError),
            );
            onLoaded?.();
            return;
          }
          doMount();
        });
      } else {
        doMount();
      }

      return {
        unmount: () => {
          cancelled = true;
          teardownSavedCardCvcForm(states);
        },
      };
    };

    return { mount };
  };
};

/** Read `data.attributes.cvc_alias` from a VGS CMP card response (parsed or JSON string). */
const readCvcAlias = (data: unknown): string | undefined => {
  let parsed: unknown = data;
  if (typeof data === "string") {
    try {
      parsed = JSON.parse(data);
    } catch {
      return undefined;
    }
  }
  return (parsed as { data?: { attributes?: { cvc_alias?: string } } } | undefined)?.data?.attributes?.cvc_alias;
};

/**
 * Re-collects the CVC into a FRESH VGS alias via the OUTBOUND CMP Card Update API
 * (CIT_SAVED_CARD_ONE_CLICK §14): vgs-collect 4.0.1's `form.updateCard(...)` PATCHes
 * `/cards/{cmpCardId}` on the CMP API host, setting a fresh CVC on the already-vaulted
 * card and returning the card with a fresh `cvc_alias`. The raw CVC never leaves the VGS
 * iframe — the mounted CVC field is applied to the card attributes SDK-side, and the
 * request goes straight to the CMP API host (NOT our backend, so no inbound REDACT route
 * and no tunnel). Mirrors the fresh-card `createCard({ auth, ... })` call; the returned
 * alias rides the confirm request as `saved_card_cvc_token`.
 *
 * Unlike `createCard` (POST /cards, which can 303-dedup to an existing card WITHOUT a
 * fresh `cvc_alias`), `updateCard` PATCHes one specific card, so the response reliably
 * carries the freshly-set CVC alias.
 */
const tokenizeSavedCardCvc = async (
  states: CardStates,
  cmpCardId: string | undefined,
): Promise<{ token?: string; error?: string }> => {
  const form = states.savedCardCvcForm;
  if (!form) {
    return { error: "Security code field is not ready — please try again" };
  }
  if (!cmpCardId) {
    return { error: "This saved card cannot re-collect its security code — please use a different card" };
  }

  let collectToken: string;
  try {
    collectToken = await fetchCollectToken(states.apiBaseUrl, states.secureToken);
  } catch (err) {
    return { error: stringifyError(err) };
  }

  // vgs-collect 4.0.1 updateCard: PATCH /cards/{cardId} with the mounted CVC field applied
  // into the card's attributes SDK-side. Same options shape as createCard (`{ auth }`) plus
  // the target `cardId`; the success callback receives the updated card object, whose fresh
  // `cvc_alias` we hand back as saved_card_cvc_token.
  //
  // Wrapped in the SAME 120s Promise.race timeout the fresh-card createCard path uses: a hung
  // updateCard (VGS never fires either callback) must reject here instead of leaving the
  // confirmSavedCard await — and the pay button — pending forever.
  const VGS_CREATE_CARD_TIMEOUT_MS = 120_000;
  let alias: string | undefined;
  try {
    let updateCardTimeoutId: ReturnType<typeof setTimeout>;
    alias = await Promise.race([
      new Promise<string | undefined>((resolve) => {
        form.updateCard(
          { cardId: cmpCardId, auth: collectToken },
          (_status, cardObject) => resolve(readCvcAlias(cardObject)),
          (err) => {
            console.error("[PaymentKit] VGS updateCard (CVC re-collection) failed:", stringifyError(err));
            resolve(undefined);
          },
        );
      }),
      new Promise<never>((_, reject) => {
        updateCardTimeoutId = setTimeout(() => reject(new Error("VGS_TIMEOUT")), VGS_CREATE_CARD_TIMEOUT_MS);
      }),
    ]).finally(() => clearTimeout(updateCardTimeoutId));
  } catch (err) {
    if (err instanceof Error && err.message === "VGS_TIMEOUT") {
      console.error("[PaymentKit] VGS updateCard (CVC re-collection) timed out");
      return { error: "Security code processing timed out — please try again" };
    }
    console.error("[PaymentKit] VGS updateCard (CVC re-collection) failed:", stringifyError(err));
    return { error: "Could not read the security code — please try again" };
  }

  return alias ? { token: alias } : { error: "Could not read the security code — please try again" };
};

/**
 * Options for the saved-card one-click confirm. Carries the same India e-mandate /
 * customer-passthrough options as a fresh-card submit, plus the customer fields the
 * confirm request needs (all optional — a returning customer is already linked to
 * the session).
 */
export type SavedCardConfirmOptions = CardSubmitOptions & {
  fields?: Partial<PaymentKitFields>;
  /**
   * The chosen saved card's CMP card id (CRD…), from the saved-cards list. Used by the
   * CVC re-collection's outbound CMP Card Update. Falls back to the id threaded into
   * state when the CVC element mounted, so passing it here is optional.
   */
  cmpCardId?: string;
};

/**
 * Confirms a saved-card one-click charge (CIT_SAVED_CARD_ONE_CLICK). Sends
 * `saved_payment_method_id` (+ the re-collected `saved_card_cvc_token` when the CVC
 * element is mounted) INSTEAD of a fresh `card_setup_intent_id`, then drives the
 * SAME real checkout loop as the fresh-card path (`runCardCheckoutLoop`) — so 3DS,
 * the processor cascade, and RBI e-mandate consent all behave identically for the
 * reuse charge. Returns the same `{ data } | { errors }` shape as the fresh submit.
 */
const defConfirmSavedCard = (states: CardStates) => {
  return async (paymentMethodId: string, opts?: SavedCardConfirmOptions): Promise<CardCheckoutResult> => {
    // A saved-card charge runs outside submit(), so it ends a pending BNPL submit itself.
    states._onPaymentStarting?.("card");
    // `_sessionConfigReady` never rejects; a terminal failure is carried on `sessionConfigError`.
    // This function is documented to return `{ data } | { errors }`, so surface that as `{ errors }`.
    if (states._sessionConfigReady) await states._sessionConfigReady;
    if (states.sessionConfigError) {
      states.timingTracker.trackFail(null, "config_error", stringifyError(states.sessionConfigError));
      return { errors: { root: "Checkout could not load. Please refresh and try again." } };
    }

    const { timingTracker, checkoutRequestId } = states;
    timingTracker.trackSubmit();

    if (!paymentMethodId) {
      timingTracker.trackFail(null, "validation_error", "Missing saved payment method id");
      return { errors: { root: "No saved card was selected" } };
    }

    const requestOptions = withRequestId(checkoutRequestId);
    const tunnelX = await TunnelXManager.createFromPenpalConnection(states.tunnelXConnection);

    // Re-collected CVC (only when the merchant's re-prompt setting mounted the CVC
    // element). PCI-safe: only the alias crosses back, never the raw digits.
    let savedCardCvcToken: string | undefined;
    if (states.savedCardCvcMounted) {
      // The chosen card's CMP id, from the confirm opts (looked up in the saved-cards
      // list) or, as a fallback, the id threaded into state when the CVC element mounted.
      const cmpCardId = opts?.cmpCardId ?? states.savedCardCvcCmpCardId;
      const cvcResult = await tokenizeSavedCardCvc(states, cmpCardId);
      if (cvcResult.error || !cvcResult.token) {
        timingTracker.trackFail(null, "card_submit_error", cvcResult.error || "CVC tokenization failed");
        return {
          errors: {
            card_cvc: "invalid",
            root: cvcResult.error || "Could not read the security code — please try again",
          },
        };
      }
      savedCardCvcToken = cvcResult.token;
    }

    // Fraud metadata + Stripe Radar session, identical to the fresh-card submit so a
    // reuse charge carries the same telemetry / dispute-deflection signals. Radar is
    // minted at init (PAY-2001); this only waits, bounded, for that mint. Best-effort —
    // undefined (and omitted below) on any failure, so the charge is never blocked.
    const stripeRadarSession = await waitForRadarSessionAtSubmit(states._radarSessionMint, {
      checkoutSessionId: states.secureToken,
      requestId: states.checkoutRequestId,
    });
    await awaitFingerprintSettled(states._fraudScriptsReady);
    const airwallexDeviceId = airwallexDeviceIdForRequest(states.checkoutRequestId);
    const fraudMetadata = {
      ...collectFraudMetadata(),
      clientVersion: states.clientVersion,
      threeDsBrowserInfo: collect3dsBrowserInfo(),
      threeDsOrigin: window.location.origin,
      threeDsReturnUrl: window.location.href,
      processorFraudInfo: {
        ...(airwallexDeviceId ? { airwallexDeviceId, airwallexFingerprintCollected: true } : {}),
        ...(stripeRadarSession ? { stripeRadarSession } : {}),
      },
    };

    const rawFields = opts?.fields;
    const customerInfo = rawFields
      ? {
          ...mapFieldsToCustomerInfo({
            ...rawFields,
            customer_name: rawFields.customer_name ?? "",
          } as PaymentKitFields),
          processorOverrides: opts?.extraParamsForCustomerCreation?.processor_overrides as
            | Record<string, { email?: string }>
            | undefined,
        }
      : {};

    const runCardCheckout = (indiaMandateConsentFields: Record<string, unknown>) =>
      tunnelX.publicEndpoints.cardCheckout(
        {
          checkoutToken: states.secureToken,
          publicCardCheckoutRequest: {
            // Saved-card source instead of a fresh setup intent. The confirm-request
            // validator enforces exactly one of the two, so cardSetupIntentId is omitted.
            savedPaymentMethodId: paymentMethodId,
            ...(savedCardCvcToken ? { savedCardCvcToken } : {}),
            customerInfo,
            fraudMetadata,
            supportsIndiaMandate: true,
            ...indiaMandateConsentFields,
          } as PublicCardCheckoutRequest,
        },
        requestOptions,
      );

    return runCardCheckoutLoop(states, tunnelX, requestOptions, opts, runCardCheckout);
  };
};

/**
 * A saved card offered on the one-click checkout — PKJS's own published shape
 * (camelCase, mirroring the backend ``PublicSavedCard``). PKJS deliberately does NOT
 * re-export the generated ``@pkg/sdk`` model, to keep its published npm surface free of
 * internal-SDK type dependencies.
 */
export type SavedCard = {
  id: string;
  brand?: string | null;
  last4?: string | null;
  expMonth?: string | null;
  expYear?: string | null;
  // Server-computed from the session's default_payment_method_id.
  isDefault?: boolean;
  // VGS CMP card id (CRD…) used ONLY to re-collect the CVC for a one-click charge via the
  // outbound CMP Card Update. Null when the card has no valid CRD id (e.g. migrated cards):
  // still listable and chargeable, just not one-click CVC-recollectable through CMP.
  cmpCardId?: string | null;
};

/** `{ data } | { errors }` — same shape as `confirmSavedCard`, so a caller branches on `"errors" in result`. */
export type ListSavedCardsResult = { data: SavedCard[] } | { errors: PaymentKitErrors };
export type SavedCardCvcRepromptResult = { data: boolean } | { errors: PaymentKitErrors };

/**
 * Lists the session customer's saved cards for the fully-PKJS one-click checkout
 * (CIT_SAVED_CARD_ONE_CLICK §14). Wraps the session-scoped, PCI-safe
 * ``GET /{checkout_token}/saved-cards`` through the same tunnel the charge uses, so a
 * client-only PKJS embedder (no merchant backend) can render a saved-card picker and
 * one-click a vaulted card — including the ``cmpCardId`` the outbound CVC re-collection
 * needs. No PAN/CVC and no chargeable processor token ever crosses.
 *
 * Returns `{ data } | { errors }` (matching ``confirmSavedCard``): a network blip while
 * rendering the picker returns `{ errors }` instead of an unhandled promise rejection, so
 * the UI can degrade to the fresh-card form.
 */
const defListSavedCards = (states: CardStates) => {
  return async (): Promise<ListSavedCardsResult> => {
    try {
      // Behave like the other card funcs: if the tokenization mode is being auto-detected, wait for
      // it. It never carries the saved-cards list — that comes from the forward below, and it never
      // rejects (a terminal failure is on `sessionConfigError`), so surface that as `{ errors }`.
      if (states._sessionConfigReady) await states._sessionConfigReady;
      if (states.sessionConfigError) return { errors: { root: "Could not load your saved cards" } };
      const tunnelX = await TunnelXManager.createFromPenpalConnection(states.tunnelXConnection);
      const res = await tunnelX.publicEndpoints.listSavedCards({ checkoutToken: states.secureToken });
      return {
        data: (res?.savedCards ?? []).map((c) => ({
          id: c.id,
          brand: c.brand,
          last4: c.last4,
          expMonth: c.expMonth,
          expYear: c.expYear,
          isDefault: c.isDefault,
          cmpCardId: c.cmpCardId,
        })),
      };
    } catch {
      return { errors: { root: "Could not load your saved cards" } };
    }
  };
};

/**
 * Reads the merchant's server-resolved saved-card CVC-reprompt flag
 * (``savedCardCvcReprompt``) so a fully-PKJS embedder can gate the CVC element on the
 * real setting instead of guessing.
 *
 * Does its OWN forward of ``getCheckoutSessionByToken`` — it deliberately does NOT
 * piggy-back on ``_sessionConfigReady`` / the ``cardTokenizationMode === undefined``
 * gate, because that fetch stores only the tokenization mode, never this flag.
 *
 * FAIL-SAFE CONTRACT: on `{ errors }` the caller must NOT treat reprompt as OFF and fire a
 * CVC-less one-click. The backend intentionally suppresses the missing-CVC `[ALERT]` for
 * saved sources and does NOT block, so a CVC-less saved-card charge captures SILENTLY
 * (lower auth rates; hard-fails only on Adyen). On `{ errors }`, retry or fall back to
 * showing the CVC field / the fresh-card form — never silent-charge CVC-less.
 */
const defGetSavedCardCvcReprompt = (states: CardStates) => {
  return async (): Promise<SavedCardCvcRepromptResult> => {
    try {
      const tunnelX = await TunnelXManager.createFromPenpalConnection(states.tunnelXConnection);
      const s = await tunnelX.publicEndpoints.getCheckoutSessionByToken({ checkoutToken: states.secureToken });
      return { data: !!s?.savedCardCvcReprompt };
    } catch {
      // Return { errors }, NOT { data: false } — a false here would let a caller
      // silent-charge CVC-less (see the fail-safe contract above).
      return { errors: { root: "Could not read the checkout configuration" } };
    }
  };
};

/**
 * Fetches a VGS CMP access token from the backend.
 * Uses AbortController with 10s timeout to prevent indefinite hangs on slow networks.
 */
const FETCH_COLLECT_TOKEN_TIMEOUT_MS = 10_000;

const fetchCollectToken = async (apiBaseUrl: string, secureToken: string): Promise<string> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_COLLECT_TOKEN_TIMEOUT_MS);

  try {
    const resp = await fetch(`${apiBaseUrl}/api/card-setup-intents/${secureToken}/collect-token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal: controller.signal,
    });
    if (!resp.ok) {
      throw new Error(`Failed to get collect token: ${resp.status}`);
    }
    const data = await resp.json();
    return data.access_token;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Creates a card via VGS CMP and sends aliases to our backend.
 *
 * Flow:
 * 1. Fetch CMP access token from our backend
 * 2. Call form.createCard() → VGS creates card, returns aliases
 * 3. Send aliases + fraud metadata to our backend via updateCardSetupIntent
 */
/**
 * Map VGS field-level error keys to PaymentKitErrors field keys.
 *
 * VGS CMP errorCallback uses short field names ("pan", "cvc", "exp-date")
 * matching the CMP field type, NOT the longer CSS-style names. Both forms
 * are mapped here for robustness.
 */
const VGS_FIELD_MAP: Record<string, keyof PaymentKitErrors> = {
  // Actual VGS CMP error keys (verified empirically)
  pan: "card_pan",
  cvc: "card_cvc",
  "exp-date": "card_exp",
  // Alternative key formats (VGS documentation references)
  "card-number": "card_pan",
  "card-security-code": "card_cvc",
  "card-expiration-date": "card_exp",
};

/**
 * Safely stringify an error for console logging.
 * VGS errors are plain objects that print as [object Object] without this.
 */
const stringifyError = (err: unknown): string => {
  if (err instanceof Error) return err.message;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
};

const submitVgsCardFields = async (
  states: CardStates,
  tunnelX: Awaited<ReturnType<typeof TunnelXManager.createFromPenpalConnection>>,
): Promise<{ errors: PaymentKitErrors; isSuccess: boolean }> => {
  const { vgsForm, cardSetupIntentId, secureToken, apiBaseUrl, checkoutRequestId } = states;
  const logPrefix = `[PaymentKit] [${checkoutRequestId}]`;
  const errors = {} as PaymentKitErrors;

  if (!vgsForm) {
    errors.card_pan = "required";
    return { errors, isSuccess: false };
  }

  if (!cardSetupIntentId) {
    errors.root = "Card setup intent not created";
    return { errors, isSuccess: false };
  }

  // Step 1: Get CMP access token from our backend
  let accessToken: string;
  const collectTokenStart = performance.now();
  try {
    accessToken = await fetchCollectToken(apiBaseUrl, secureToken);
    const collectTokenDuration = Math.round(performance.now() - collectTokenStart);
    AnalyticsService.capture("vgs_collect_token_timing", {
      checkout_session_id: secureToken,
      request_id: checkoutRequestId,
      duration_ms: collectTokenDuration,
      success: true,
    });
  } catch (err) {
    const collectTokenDuration = Math.round(performance.now() - collectTokenStart);
    const isAbortError = err instanceof DOMException && err.name === "AbortError";
    AnalyticsService.capture("vgs_collect_token_timing", {
      checkout_session_id: secureToken,
      request_id: checkoutRequestId,
      duration_ms: collectTokenDuration,
      success: false,
      error_type: isAbortError ? "FETCH_TIMEOUT" : "FETCH_ERROR",
    });
    console.error(`${logPrefix} VGS auth token fetch failed:`, stringifyError(err));
    errors.root = isAbortError
      ? "Card authentication timed out — please try again"
      : "Card authentication failed — please try again";
    return { errors, isSuccess: false };
  }

  // Step 2: Create card via VGS CMP — returns aliases for PAN/CVC, plain text expiry
  let cardResult: VGSCreateCardResponse;
  const VGS_CREATE_CARD_TIMEOUT_MS = 120_000;
  const createCardStart = performance.now();
  try {
    let createCardTimeoutId: ReturnType<typeof setTimeout>;
    cardResult = await Promise.race([
      new Promise<VGSCreateCardResponse>((resolve, reject) => {
        vgsForm.createCard(
          {
            auth: accessToken,
            data: { cardholder: {} },
          },
          (_status, cardObject) => resolve(cardObject),
          (error) => reject(error),
        );
      }),
      new Promise<never>((_, reject) => {
        createCardTimeoutId = setTimeout(() => reject(new Error("VGS_TIMEOUT")), VGS_CREATE_CARD_TIMEOUT_MS);
      }),
    ]).finally(() => clearTimeout(createCardTimeoutId));

    // Emit timing event on success
    const createCardDuration = Math.round(performance.now() - createCardStart);
    AnalyticsService.capture("vgs_create_card_timing", {
      checkout_session_id: secureToken,
      request_id: checkoutRequestId,
      duration_ms: createCardDuration,
      success: true,
    });
  } catch (err) {
    const createCardDuration = Math.round(performance.now() - createCardStart);
    const errStr = stringifyError(err);
    console.error(`${logPrefix} VGS createCard failed:`, errStr);

    // Distinguish timeout from VGS field validation errors
    if (err instanceof Error && err.message === "VGS_TIMEOUT") {
      AnalyticsService.capture("vgs_create_card_timing", {
        checkout_session_id: secureToken,
        request_id: checkoutRequestId,
        duration_ms: createCardDuration,
        success: false,
        error_type: "VGS_TIMEOUT",
      });
      errors.root = "Card processing timed out — please try again";
      return { errors, isSuccess: false };
    }

    // VGS errorCallback returns field-level errors as a plain object,
    // e.g. { "card-number": { errorMessages: ["Card number is not valid"] } }
    // Map them to per-field errors so inline validation shows on the right field.
    if (err && typeof err === "object" && !(err instanceof Error)) {
      const vgsErrors = err as Record<string, unknown>;
      for (const [vgsKey, fieldKey] of Object.entries(VGS_FIELD_MAP)) {
        if (vgsKey in vgsErrors) {
          const fieldErr = vgsErrors[vgsKey] as Record<string, unknown> | undefined;
          const messages = fieldErr?.errorMessages;
          errors[fieldKey] = Array.isArray(messages) ? messages[0] : "invalid";
        }
      }
      if (Object.keys(errors).length > 0) {
        AnalyticsService.capture("vgs_create_card_timing", {
          checkout_session_id: secureToken,
          request_id: checkoutRequestId,
          duration_ms: createCardDuration,
          success: false,
          error_type: "VGS_VALIDATION_ERROR",
        });
        return { errors, isSuccess: false };
      }
    }

    // Generic VGS error
    AnalyticsService.capture("vgs_create_card_timing", {
      checkout_session_id: secureToken,
      request_id: checkoutRequestId,
      duration_ms: createCardDuration,
      success: false,
      error_type: "VGS_ERROR",
    });
    errors.root = "Card validation failed — please check your card details and try again";
    return { errors, isSuccess: false };
  }

  // Step 3: Extract aliases and expiry from CMP response
  if (!cardResult?.data?.attributes) {
    console.error(`${logPrefix} VGS createCard returned invalid response:`, stringifyError(cardResult));
    errors.root = "Card processing returned an invalid response — please try again";
    return { errors, isSuccess: false };
  }
  const { pan_alias, cvc_alias, exp_month, exp_year } = cardResult.data.attributes;

  if (!cvc_alias) {
    console.warn(
      `${logPrefix} VGS createCard response missing cvc_alias (303 dedup):`,
      JSON.stringify(cardResult.data.attributes),
    );
  }

  const fullYear = exp_year < 100 ? 2000 + exp_year : exp_year;
  const formattedExp = `${String(exp_month).padStart(2, "0")}/${fullYear}`;

  // Capture CMP card ID for network token tracking
  const cmpCardId = cardResult.data.id;

  // Step 4: Send aliases + fraud metadata to our backend.
  // CVC is sent as a VGS alias from createCard. Additionally, the CVC is
  // submitted via the vault inbound proxy (REDACT route) as a fallback for
  // the 303 deduplication path where VGS returns an existing card without
  // a fresh cvc_alias.
  try {
    const updateIntentCall = tunnelX.publicEndpoints.updateCardSetupIntent({
      checkoutToken: secureToken,
      cardSetupIntentId: cardSetupIntentId,
      updateCardSetupIntentReq: {
        cardPan: pan_alias,
        cardExp: formattedExp,
        ...(cvc_alias ? { cardCvc: cvc_alias } : {}),
        cmpCardId: cmpCardId,
        // Card is the VGS 3DS path. browserInfo keeps the general fraud telemetry;
        // the VGS 3DS fingerprint fields go in threeDsBrowserInfo, mirroring the
        // cardCheckout call above. (This CSI fraudMetadata is telemetry-only — the
        // Phase A initialize_fingerprint does not read browser_info — but keep the
        // split symmetric with Site A so neither blob mixes the two field sets.)
        fraudMetadata: {
          ...collectFraudMetadata(),
          threeDsBrowserInfo: collect3dsBrowserInfo(),
        },
      },
    });

    const proxyCvcCall = new Promise<void>((resolve, reject) => {
      if (!vgsForm.submit) {
        reject(new Error("VGS form.submit not available"));
        return;
      }
      vgsForm.submit(
        `/api/card-setup-intents/${secureToken}/${cardSetupIntentId}`,
        {
          method: "post",
          headers: { "Content-Type": "application/json" },
          data: (fields: Record<string, unknown>) => ({ card_cvc: fields.cvc }),
        },
        (_status: number) => resolve(),
        (err: unknown) => reject(err),
      );
    });

    const [updateResult] = await Promise.all([updateIntentCall, proxyCvcCall]);

    // VGS 3DS Phase A: if the backend returned device-fingerprinting HTML, inject it
    // as a hidden background iframe — fully non-blocking, never a requires-action
    // state. The ACS form auto-submits on load and posts to the directory server
    // while the shopper finishes checkout.
    const fingerprintHtml = updateResult?.deviceFingerprintingHtml;
    if (fingerprintHtml) {
      injectFingerprintIframe(states, fingerprintHtml);
    }
  } catch (err) {
    console.error(`${logPrefix} updateCardSetupIntent failed:`, stringifyError(err));
    errors.root = "Failed to save card details — please try again";
    return { errors, isSuccess: false };
  }

  return { errors: {}, isSuccess: true };
};

/**
 * Inject a hidden VGS 3DS device-fingerprint iframe (non-blocking). The iframe id
 * is registered on `states` so the card method's cleanup() can remove it.
 */
const injectFingerprintIframe = (states: CardStates, srcdocHtml: string): void => {
  if (typeof document === "undefined") return;
  const iframe = document.createElement("iframe");
  const id = `pk-3ds-fingerprint-${Date.now()}`;
  iframe.id = id;
  iframe.setAttribute("srcdoc", srcdocHtml);
  iframe.setAttribute("aria-hidden", "true");
  iframe.width = "0";
  iframe.height = "0";
  iframe.style.display = "none";
  document.body.appendChild(iframe);
  if (!states.fingerprintIframeIds) {
    states.fingerprintIframeIds = [];
  }
  states.fingerprintIframeIds.push(id);
  console.log("Injected VGS fingerprint iframe");
};

const submitCardFields = async (states: CardStates) => {
  const errors = {} as PaymentKitErrors;
  const { cardSetupIntentId, cardInputConnections } = states;

  const submitPromises = Object.entries(cardInputConnections).map(async ([_type, connection]) => {
    const type = _type as PenpalCardInputType;

    const remote = await connection.promise;
    const result = await remote.onSubmit(cardSetupIntentId || "");

    if ("error" in result) {
      errors[type] = result.error;
    }
  });

  await Promise.allSettled(submitPromises);

  return {
    errors,
    isSuccess: Object.keys(errors).length === 0,
  };
};

const validateCardFields = async (states: CardStates) => {
  const errors: PaymentKitErrors = {};
  const { cardInputConnections } = states;

  const validatePromises = Object.entries(cardInputConnections).map(async ([_type, connection]) => {
    const type = _type as PenpalCardInputType;

    if (!connection) {
      errors[type] = "penpal_not_connected";
      return;
    }

    const remote = await connection.promise;
    const errorMsg = await remote.onValidate();

    if (errorMsg) {
      errors[type] = errorMsg;
    }
  });

  await Promise.allSettled(validatePromises);

  return {
    errors,
    isSuccess: Object.keys(errors).length === 0,
  };
};

const CardPaymentMethod = definePaymentMethod((paymentKitStates) => {
  // Object.create() creates prototype delegation: reads fall through to the shared
  // paymentKitStates (preserving auto-detected VGS config), writes stay local.
  // This prevents card-specific properties (cardInputConnections, vgsForm, etc.)
  // from leaking onto the shared paymentKitStates object.
  const localStates = Object.create(paymentKitStates) as CardStates;
  localStates.cardInputConnections = {};

  return {
    name: "card",
    externalFuncs: {
      createElement: defCreateElement(localStates),
      // Saved-card one-click (CIT_SAVED_CARD_ONE_CLICK §14). List the returning
      // customer's vaulted cards and read the CVC-reprompt setting through PKJS (so a
      // client-only embedder needs no merchant backend), mount the CVC-only element when
      // the merchant's re-prompt setting is on, then charge a vaulted card with
      // confirmSavedCard(paymentMethodId).
      listSavedCards: defListSavedCards(localStates),
      getSavedCardCvcReprompt: defGetSavedCardCvcReprompt(localStates),
      createSavedCardCvcElement: defCreateSavedCardCvcElement(localStates),
      confirmSavedCard: defConfirmSavedCard(localStates),
    },
    internalFuncs: {
      submitPayment: defSubmitPayment(localStates),
      cleanup: () => {
        teardownVgsForm(localStates);
        teardownSavedCardCvcForm(localStates);
        // Clean up all card input iframe connections. Guarded per-connection so one
        // connection's destroy() throwing can't skip the remaining connections or the
        // fingerprint-iframe cleanup below (same class of bug as the VGS guard above).
        for (const [type, connection] of Object.entries(localStates.cardInputConnections)) {
          try {
            connection?.destroy();
          } catch (err) {
            reportCleanupFailure(`card:connection_destroy:${type}`, err);
          }
        }
        localStates.cardInputConnections = {};
        // Remove any hidden VGS 3DS fingerprint iframes.
        for (const iframeId of localStates.fingerprintIframeIds ?? []) {
          document.getElementById(iframeId)?.remove();
        }
        localStates.fingerprintIframeIds = [];
      },
    },
  };
});

export default CardPaymentMethod;
